import { useState } from "react";
import { Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { ShowreelProvider } from "@/components/site/Showreel";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Footer } from "@/components/site/Footer";
import { FestivalHeader } from "@/components/site/FestivalHeader";
import { JoinDialog } from "@/components/site/JoinDialog";
import { resolveTheme } from "@/lib/festivalTheme";

const DEFAULT_ABOUT = {
  title: "À propos du Karams Festival",
  lead: "Un festival de cinéma engagé, porté par la nouvelle génération.",
  body: "Le Karams Festival est un rendez-vous cinématographique dédié aux films qui portent un regard neuf sur le monde.\n\nIl existe pour offrir une scène aux jeunes réalisateur·rice·s, faire dialoguer les cultures et sensibiliser le public à travers des œuvres fortes et sincères.\n\nNotre objectif : célébrer le cinéma comme un outil d'émotion, de réflexion et de changement, et accompagner les talents de demain de l'écriture jusqu'à la projection.",
  studioLinkLabel: "Un festival organisé par Karams Studio",
  showStudioLink: true,
};

const AboutBody = () => {
  const { content } = useContent();
  const archive = content.festivalArchive || {};
  const eds = (archive.editions || []).filter((e) => e && !e.hidden);
  const current = eds.find((e) => e.current) || eds.sort((a, b) => (parseInt(b.year, 10) || 0) - (parseInt(a.year, 10) || 0))[0];
  const themeVars = resolveTheme(archive.theme, current);
  const about = { ...DEFAULT_ABOUT, ...((content.festival || {}).about || {}) };
  const paras = String(about.body || "").split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
  const [joinOpen, setJoinOpen] = useState(false);

  return (
    <div className="App festival-universe min-h-screen flex flex-col" style={{ ...themeVars, backgroundColor: "var(--fx-bg)", color: "var(--fx-text)" }}>
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <FestivalHeader brand={content.brand || {}} socials={content.contact?.socials || []} onApply={() => setJoinOpen(true)} />

      <main className="flex-1 pt-[110px] pb-20" data-testid="festival-about-page">
        <div className="max-w-[860px] mx-auto px-5 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-3 font-semibold">Karams Festival</div>
          <h1 className="font-serif text-4xl md:text-6xl text-white tracking-tight mb-4">{about.title}</h1>
          {about.lead && <p className="text-lg md:text-xl text-white/80 font-light mb-10 max-w-2xl">{about.lead}</p>}

          <div className="space-y-5 text-base md:text-lg leading-relaxed text-white/75 font-light" data-testid="festival-about-body">
            {paras.map((p, i) => <p key={i}>{p}</p>)}
          </div>

          {about.showStudioLink && (
            <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="festival-about-studio">
              <span className="text-sm text-white/50">{about.studioLinkLabel}</span>
              <Link to="/" data-testid="festival-about-studio-link"
                className="inline-flex items-center gap-2 px-5 h-10 rounded-md border border-white/20 text-[11px] tracking-[0.14em] uppercase text-white/80 hover:border-electric hover:text-electric transition-colors">
                Découvrir Karams Studio <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} type="festival" refId="festival"
        refTitle="Karams Festival" roles={(content.festival || {}).joinRoles || []}
        notice={(content.festival || {}).participation?.notice} documents={(content.festival || {}).participation?.documents}
        heading="Soumettre ma candidature" intro="Candidatez, devenez bénévole ou partenaire du Karams Festival." />
    </div>
  );
};

export default function FestivalAboutPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <AboutBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
